/*----------------------------------
- PRIVACY PAGE CONTENT CATALOG (SSOT)
----------------------------------*/

import { AGENDA_MAP_TILE_ORIGIN } from '@uniquedomains/components/agenda/mapModel';

import type { LegalPageContent } from '@/client/catalogs/landing/legal/types';

/**
 * The processor list is read from what the code actually loads, not from what
 * we would like it to load:
 * - Google Analytics 4 and Crisp, `apps/website/client/services/metrics/Component.tsx`
 * - Mixpanel with autocapture and full session recording, `apps/website/client/services/metrics/mixpanel.ts`
 * - the Brevo web tracker, `packages/components/runtime/brevoTracker.ts`, which also runs
 *   in the product app and identifies a signed-in visitor by email
 * - Stripe for payments and Brevo for email, from the product service config
 * - DeBounce for the signup address check and Discord for operator notifications,
 *   both from the product service config
 * - OpenFreeMap, whose style, tiles, sprites and glyphs the map pane of the agenda and of
 *   the product calendar loads from `AGENDA_MAP_TILE_ORIGIN` once the map is shown
 *   (`packages/components/agenda/mapModel.ts`); the host below is read from that constant
 *
 * The retention wording matches what an account deletion actually does
 * (`docs/features/account-deletion`): it reaches every processor above except the
 * invoices Stripe keeps for tax law and messages already delivered.
 *
 * A consent control is not built yet, so this page states plainly what loads on
 * arrival rather than claiming a consent basis the site does not collect. When
 * the consent gate ships, the wording in `analytics` and `cookies` changes with it.
 */

export const PRIVACY_PAGE_CONTENT: LegalPageContent = {
    meta: {
        pageTitle: 'Privacy Policy | Unique Domains',
        metaDescription:
            'What Unique Domains collects, which processors we use, how long we keep data, and how to exercise your rights under the GDPR.',
    },
    hero: {
        heading: 'Privacy Policy',
        intro: 'This page lists exactly what we collect, who processes it on our behalf, and what you can ask us to do with it.',
    },
    sections: [
        {
            id: 'controller',
            heading: '1. Who is responsible',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'The company identified at the top of this page is the data controller for the information described here. Write to the contact address listed there with any privacy question or request.',
                },
            ],
        },
        {
            id: 'what-we-collect',
            heading: '2. What we collect',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'Account data: your email address, your name if you provide one, the sign-in method you used, and the persona and preferences you choose during setup.',
                        'Plan and billing data: your plan, your credit balance and its history, and the payment records Stripe returns to us. We never receive your full card number.',
                        'Product data: the searches, saved searches, Radars and their briefs, watchlists and notes you create.',
                        'Usage data: the pages you open, the actions you take, your IP address, your browser and device, and the referring page.',
                        'Support data: the messages you send us through the chat widget or by email.',
                    ],
                },
            ],
        },
        {
            id: 'analytics',
            heading: '3. Analytics, chat and measurement',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'We want to be specific here, because some of this is more detailed than visitors expect. When you open the public website, these load automatically:',
                },
                {
                    kind: 'list',
                    items: [
                        'Google Analytics 4, which records page views and events and sets cookies to recognise a returning browser.',
                        'Mixpanel, which records product events automatically and captures session recordings of your visit, including the pages you view and the way you move through them.',
                        'Crisp, our support chat, which sets cookies so a conversation continues across pages.',
                        'The Brevo tracker, which records the pages you view and sets a cookie so we can tell whether an email we sent you led to a visit. If you are signed in, your email address is sent to Brevo so the visit is attached to your contact record.',
                    ],
                },
                {
                    kind: 'paragraph',
                    text: 'We use this to understand which pages work, where people get stuck, and which changes help. We do not sell it, and we do not use it for advertising.',
                },
                {
                    kind: 'paragraph',
                    text: 'We are adding a consent control so you can refuse analytics and session recording before they load. Until it ships, you can block them with your browser privacy settings, a tracker blocker, or by writing to us and asking us to delete what we hold about your visits.',
                },
            ],
        },
        {
            id: 'cookies',
            heading: '4. Cookies',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'Necessary cookies keep you signed in and carry a request identifier we use to trace errors. The site does not work without them.',
                        'Analytics cookies are set by Google Analytics, Mixpanel and Brevo, as described above.',
                        'Support cookies are set by Crisp so your chat history follows you between pages.',
                    ],
                },
                {
                    kind: 'paragraph',
                    text: 'You can clear or block cookies in your browser at any time. Blocking necessary cookies will sign you out.',
                },
            ],
        },
        {
            id: 'legal-bases',
            heading: '5. Why we are allowed to process it',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'To perform our contract with you: your account, your plan, your billing and the features you use.',
                        'To meet a legal obligation: keeping accounting and tax records.',
                        'For our legitimate interest: keeping the service secure, preventing abuse, and understanding how the product is used.',
                        'With your consent: marketing emails, which you can stop from the footer of any email we send.',
                    ],
                },
            ],
        },
        {
            id: 'processors',
            heading: '6. Who processes data for us',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'Stripe, for payments and subscription management.',
                        'Brevo, for transactional email, the newsletter, alert delivery, and our internal error reports. Those reports can include your account email, your IP address, and the request that failed.',
                        'Google Analytics, for website measurement.',
                        'Mixpanel, for product analytics and session recording.',
                        'DeBounce, which checks at signup whether your email address can receive mail.',
                        'Discord, where we receive internal notifications about new accounts and payments. They include your account email.',
                        'Crisp, for support chat.',
                        `OpenFreeMap, for the map on the agenda and calendar pages. When you open the map, your browser loads it from ${new URL(AGENDA_MAP_TILE_ORIGIN).host}, which receives your IP address.`,
                        'Anthropic, for generating the editorial content published in Domaining News. Your personal data is not sent for this purpose.',
                        'Our hosting and database providers, which store the data described here.',
                    ],
                },
            ],
        },
        {
            id: 'retention',
            heading: '7. How long we keep it',
            blocks: [
                {
                    kind: 'list',
                    items: [
                        'Account and product data: for as long as your account exists. When you ask us to delete your account, we erase it from our database and from our email, analytics and payment processors in one go.',
                        'Billing records: for the period our accounting and tax obligations require. Stripe keeps the invoices and payment records of a deleted account for that reason.',
                        'Internal notifications we have already received about your account, by email or in Discord, cannot be recalled after deletion.',
                        'Usage and analytics data: according to the retention set in each analytics tool, and no longer than needed to understand product usage.',
                        'Support conversations: for as long as they are useful to answer follow-up questions.',
                    ],
                },
            ],
        },
        {
            id: 'transfers',
            heading: '8. Transfers outside the EU',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'Some of the processors listed above are based outside the European Union. Where that is the case, the transfer relies on the European Commission standard contractual clauses or on an adequacy decision.',
                },
            ],
        },
        {
            id: 'your-rights',
            heading: '9. Your rights',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'If you are in the European Union or the United Kingdom you can ask us to give you a copy of your data, correct it, delete it, restrict how we use it, or object to our use of it. You can also ask for your data in a portable format.',
                },
                {
                    kind: 'paragraph',
                    text: 'Write to the contact address at the top of this page. We answer within one month. If you are not satisfied, you can complain to the data protection authority in the country where you live.',
                },
            ],
        },
        {
            id: 'children',
            heading: '10. Children',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'The service is for people aged 18 and over. We do not knowingly collect data about children.',
                },
            ],
        },
        {
            id: 'changes',
            heading: '11. Changes to this page',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'When we change how we handle data, we update this page and change the review date shown at the top. If a change is significant, we tell account holders by email.',
                },
            ],
        },
    ],
};
